import { EditOutlined, PlusOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import {
	Alert,
	Button,
	Drawer,
	Popconfirm,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { AssignmentModal } from "../components/AssignmentModal";
import { ScheduleFormModal } from "../components/ScheduleFormModal";
import {
	useAssignSchedule,
	useCreateSchedule,
	useRemoveScheduleAssignment,
	useScheduleAssignments,
	useSchedulesList,
	useSetScheduleActive,
	useUpdateSchedule,
} from "../hooks/useSchedules";
import type { Schedule, ScheduleAssignment, ScheduleInput } from "../types";

const { Title } = Typography;
const DRAWER_WIDTH = 720;

/** Naqshning qisqa tavsifi — jadvalda bitta ustunga sig'ishi kerak. */
function describePattern(schedule: Schedule, t: TFunction): string {
	if (schedule.patternKind === "weekly") {
		const days = (schedule.weekdays ?? [])
			.map((day) => t(`weekdayShort.${day}`, { defaultValue: String(day) }))
			.join(", ");

		return t("pattern.weekly", { days });
	}

	const workDays = schedule.workDays ?? 0;
	const restDays = (schedule.cycleDays ?? 0) - workDays;

	return t("pattern.cyclic", { workDays, restDays, anchorDate: schedule.anchorDate });
}

interface ActiveSwitchProps {
	schedule: Schedule;
	isPending: boolean;
	onToggle: (schedule: Schedule) => void;
}

function ScheduleActiveSwitch({ schedule, isPending, onToggle }: ActiveSwitchProps) {
	const { t } = useTranslation("schedules");
	const { isActive } = schedule;

	return (
		<PermissionGate
			resource="schedules"
			action="update"
			fallback={
				<Tag color={isActive ? "green" : "default"}>
					{isActive
						? t("schedulesPage.activeSwitch.booleanYes")
						: t("schedulesPage.activeSwitch.booleanNo")}
				</Tag>
			}
		>
			<Popconfirm
				title={
					isActive
						? t("schedulesPage.activeSwitch.disableTitle")
						: t("schedulesPage.activeSwitch.enableTitle")
				}
				description={t("schedulesPage.activeSwitch.description")}
				okText={t("schedulesPage.activeSwitch.confirmOk")}
				cancelText={t("schedulesPage.activeSwitch.cancel")}
				onConfirm={() => onToggle(schedule)}
			>
				<Switch checked={isActive} loading={isPending} />
			</Popconfirm>
		</PermissionGate>
	);
}

interface RowActionsProps {
	schedule: Schedule;
	onShowAssignments: (schedule: Schedule) => void;
	onEdit: (schedule: Schedule) => void;
}

function ScheduleRowActions({ schedule, onShowAssignments, onEdit }: RowActionsProps) {
	const { t } = useTranslation("schedules");

	return (
		<Space>
			<PermissionGate resource="schedules" action="read">
				<Button
					type="text"
					icon={<UsergroupAddOutlined />}
					title={t("schedulesPage.rowActions.assignments")}
					onClick={() => onShowAssignments(schedule)}
				/>
			</PermissionGate>
			<PermissionGate resource="schedules" action="update">
				<Button
					type="text"
					icon={<EditOutlined />}
					title={t("schedulesPage.rowActions.edit")}
					onClick={() => onEdit(schedule)}
				/>
			</PermissionGate>
		</Space>
	);
}

interface AssignmentsDrawerProps {
	schedule: Schedule | null;
	onClose: () => void;
	onAssign: () => void;
}

function ScheduleAssignmentsDrawer({ schedule, onClose, onAssign }: AssignmentsDrawerProps) {
	const { t } = useTranslation("schedules");
	const scheduleId = schedule?.id ?? null;
	const { data: assignments = [], isLoading } = useScheduleAssignments(scheduleId);
	const removeAssignment = useRemoveScheduleAssignment(scheduleId);

	const columns: ColumnsType<ScheduleAssignment> = [
		{
			title: t("assignments.columns.target"),
			key: "target",
			render: (_, row) =>
				row.employeeId
					? t("assignments.targetEmployee", { id: row.employeeId })
					: t("assignments.targetDepartment", { id: row.departmentId }),
		},
		{ title: t("assignments.columns.start"), dataIndex: "validFrom", key: "validFrom" },
		{
			title: t("assignments.columns.end"),
			dataIndex: "validTo",
			key: "validTo",
			render: (validTo: string | null) =>
				validTo ?? <Tag color="green">{t("assignments.unlimited")}</Tag>,
		},
		{
			title: t("assignments.columns.actions"),
			key: "actions",
			align: "right",
			render: (_, row) => (
				<PermissionGate resource="schedules" action="delete">
					<Popconfirm
						title={t("assignments.remove.title")}
						description={t("assignments.remove.description")}
						okText={t("assignments.remove.ok")}
						cancelText={t("assignments.remove.cancel")}
						okButtonProps={{ danger: true }}
						onConfirm={() => removeAssignment.mutate(row.id)}
					>
						<Button type="text" danger>
							{t("assignments.remove.button")}
						</Button>
					</Popconfirm>
				</PermissionGate>
			),
		},
	];

	return (
		<Drawer
			open={schedule !== null}
			width={DRAWER_WIDTH}
			title={
				schedule
					? t("assignments.drawerTitle", { name: schedule.name })
					: t("assignments.drawerTitleDefault")
			}
			onClose={onClose}
			destroyOnHidden
			extra={
				<PermissionGate resource="schedules" action="create">
					<Button type="primary" onClick={onAssign}>
						{t("assignments.assign")}
					</Button>
				</PermissionGate>
			}
		>
			<Table<ScheduleAssignment>
				columns={columns}
				dataSource={assignments}
				rowKey="id"
				loading={isLoading}
				pagination={false}
				size="small"
			/>
		</Drawer>
	);
}

export function SchedulesPage() {
	const { t } = useTranslation("schedules");
	const [isActive, setIsActive] = useState(true);
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<Schedule | null>(null);
	const [assignmentsSchedule, setAssignmentsSchedule] = useState<Schedule | null>(null);
	const [assignOpen, setAssignOpen] = useState(false);

	const { data: schedules = [], isLoading, isError } = useSchedulesList(isActive);
	const createSchedule = useCreateSchedule();
	const updateSchedule = useUpdateSchedule();
	const setScheduleActive = useSetScheduleActive();
	const assignSchedule = useAssignSchedule(assignmentsSchedule?.id ?? null);

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
	};

	const openCreateForm = () => {
		setEditing(null);
		setFormOpen(true);
	};

	const openEditForm = (schedule: Schedule) => {
		setEditing(schedule);
		setFormOpen(true);
	};

	const handleSubmit = (input: ScheduleInput) => {
		if (editing) {
			updateSchedule.mutate({ id: editing.id, input }, { onSuccess: closeForm });
		} else {
			createSchedule.mutate(input, { onSuccess: closeForm });
		}
	};

	/**
	 * ⚠️ Filtr MAJBURIY boshqaruv, bezak emas: `GET /schedules` standarti
	 * `isActive=true`, ya'ni faolsizlantirilgan jadval ro'yxatdan yo'qoladi va
	 * uni qayta yoqish uchun `id` sini olishning boshqa yo'li yo'q.
	 */
	const activeFilterOptions = [
		{ value: true, label: t("activeFilter.active") },
		{ value: false, label: t("activeFilter.inactive") },
	];

	const columns: ColumnsType<Schedule> = [
		{ title: t("schedulesPage.columns.name"), dataIndex: "name", key: "name" },
		{
			title: t("schedulesPage.columns.shift"),
			dataIndex: "shiftType",
			key: "shiftType",
			render: (shiftType: Schedule["shiftType"]) => (
				<Tag color={shiftType === "tungi" ? "purple" : "blue"}>{t(`shiftType.${shiftType}`)}</Tag>
			),
		},
		{
			title: t("schedulesPage.columns.pattern"),
			key: "patternKind",
			render: (_, schedule) => (
				<Space orientation="vertical" size={0}>
					<span>{t(`patternKind.${schedule.patternKind}`)}</span>
					<span className="text-gray-500 text-xs">{describePattern(schedule, t)}</span>
				</Space>
			),
		},
		{
			title: t("schedulesPage.columns.time"),
			key: "time",
			render: (_, schedule) => (
				<Space>
					<span>
						{schedule.startTime}–{schedule.endTime}
					</span>
					{schedule.crossesMidnight && <Tag color="gold">{t("schedulesPage.crossesMidnight")}</Tag>}
				</Space>
			),
		},
		{
			title: t("schedulesPage.columns.active"),
			dataIndex: "isActive",
			key: "isActive",
			render: (_, schedule) => (
				<ScheduleActiveSwitch
					schedule={schedule}
					isPending={setScheduleActive.isPending}
					onToggle={(row) => setScheduleActive.mutate({ id: row.id, isActive: !row.isActive })}
				/>
			),
		},
		{
			title: t("schedulesPage.columns.actions"),
			key: "actions",
			align: "right",
			render: (_, schedule) => (
				<ScheduleRowActions
					schedule={schedule}
					onShowAssignments={setAssignmentsSchedule}
					onEdit={openEditForm}
				/>
			),
		},
	];

	return (
		<div>
			<div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
				<Title level={3} className="!mb-0">
					{t("schedulesPage.title")}
				</Title>
				<PermissionGate resource="schedules" action="create">
					<Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
						{t("schedulesPage.newSchedule")}
					</Button>
				</PermissionGate>
			</div>

			{isError && (
				<Alert
					className="mb-4"
					type="error"
					showIcon
					title={t("schedulesPage.loadError")}
					description={t("schedulesPage.loadErrorDescription")}
				/>
			)}

			<Space wrap className="mb-4">
				<Select
					value={isActive}
					options={activeFilterOptions}
					onChange={setIsActive}
					style={{ width: 160 }}
				/>
			</Space>

			<Table<Schedule>
				columns={columns}
				dataSource={schedules}
				rowKey="id"
				loading={isLoading}
				pagination={false}
				scroll={{ x: "max-content" }}
			/>

			<ScheduleFormModal
				open={formOpen}
				editing={editing}
				isSubmitting={createSchedule.isPending || updateSchedule.isPending}
				onCancel={closeForm}
				onSubmit={handleSubmit}
			/>

			<ScheduleAssignmentsDrawer
				schedule={assignmentsSchedule}
				onClose={() => setAssignmentsSchedule(null)}
				onAssign={() => setAssignOpen(true)}
			/>

			<AssignmentModal
				open={assignOpen}
				schedule={assignmentsSchedule}
				isSubmitting={assignSchedule.isPending}
				onCancel={() => setAssignOpen(false)}
				onSubmit={(input) =>
					assignSchedule.mutate(input, { onSuccess: () => setAssignOpen(false) })
				}
			/>
		</div>
	);
}
